import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';

/**
 * Acrescenta `createdBy` (POST) ou `updatedBy` (PUT/DELETE/PATCH) na query string
 * nas chamadas aos microserviços (`environment.microservices` + `apiUrl` absoluto),
 * para o backend preencher logs de auditoria.
 *
 * Também cobre `apiUrl` relativo (ex.: `/api` com proxy).
 * Auth é ignorado (login/register não devem carregar auditoria de usuário).
 *
 * Identificador: username/email do usuário logado → claims do JWT → `lastLoggedUsername`.
 */
@Injectable()
export class StockAuditInterceptor implements HttpInterceptor {
  private readonly auditBaseUrls: readonly string[];
  private readonly relativePrefixes: readonly string[];
  private readonly skipPrefixes: readonly string[];
  private readonly auditMethods = ['post', 'put', 'delete', 'patch'];

  constructor(private authService: AuthService) {
    const { bases, relative } = this.collectAuditRoots();
    this.auditBaseUrls = bases;
    this.relativePrefixes = relative;
    const authRaw =
      (environment.microservices as Record<string, string | undefined>)?.['auth'] ?? '';
    const authBase = this.normalizeMicroserviceBase(authRaw);
    this.skipPrefixes = authBase ? [authBase] : [];
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const method = request.method?.toLowerCase() ?? '';
    if (!this.auditMethods.includes(method)) {
      return next.handle(request);
    }

    const url = request.url;
    if (!this.isAuditableTarget(url)) {
      return next.handle(request);
    }

    const username = this.resolveAuditActor();
    if (!username) {
      return next.handle(request);
    }

    const param = method === 'post' ? 'createdBy' : 'updatedBy';
    if (!this.auditParamMissing(url, param)) {
      return next.handle(request);
    }

    const encoded = encodeURIComponent(username);
    const separator = url.includes('?') ? '&' : '?';
    const urlWithAudit = `${url}${separator}${param}=${encoded}`;
    const cloned = request.clone({ url: urlWithAudit });
    return next.handle(cloned);
  }

  private collectAuditRoots(): {
    bases: string[];
    relative: string[];
  } {
    const baseSet = new Set<string>();
    const relativeSet = new Set<string>();

    const ms = environment.microservices as Record<string, unknown> | undefined;
    if (ms) {
      for (const v of Object.values(ms)) {
        const normalized = this.normalizeMicroserviceBase(String(v ?? ''));
        if (!normalized) continue;
        if (normalized.startsWith('/')) {
          relativeSet.add(normalized);
        } else {
          baseSet.add(normalized);
        }
      }
    }

    const apiUrl = typeof environment.apiUrl === 'string' ? environment.apiUrl.trim() : '';
    if (apiUrl) {
      if (apiUrl.startsWith('/')) {
        relativeSet.add(apiUrl.replace(/\/+$/, '') || '/');
      } else {
        const n = this.normalizeMicroserviceBase(apiUrl);
        if (n) baseSet.add(n);
      }
    }

    return {
      bases: Array.from(baseSet),
      relative: Array.from(relativeSet)
    };
  }

  /** Remove trailing slashes e query string (`?code=` etc.) para comparar prefixo da URL. */
  private normalizeMicroserviceBase(raw: string): string {
    let s = raw.trim();
    if (!s) return '';
    const q = s.indexOf('?');
    if (q >= 0) {
      s = s.slice(0, q);
    }
    return s.replace(/\/+$/, '');
  }

  private isAuditableTarget(url: string): boolean {
    if (this.skipPrefixes.some((p) => p && url.startsWith(p))) {
      return false;
    }
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return this.auditBaseUrls.some((base) => base && url.startsWith(base));
    }
    return this.relativePrefixes.some(
      (p) => url === p || url.startsWith(`${p}/`)
    );
  }

  private auditParamMissing(url: string, param: string): boolean {
    try {
      const u = new URL(url, 'http://local.invalid');
      return !u.searchParams.has(param);
    } catch {
      return !url.toLowerCase().includes(`${param.toLowerCase()}=`);
    }
  }

  private resolveAuditActor(): string | null {
    const u = this.authService.currentUserValue as Record<string, unknown> | null;
    if (u) {
      const fromUser =
        this.str(u['username']) ||
        this.str(u['Username']) ||
        this.str(u['email']) ||
        this.str(u['Email']);
      if (fromUser) return fromUser;
    }
    const fromJwt =
      this.authService.getUniqueNameFromToken()?.trim() ||
      this.authService.getUserNameFromToken()?.trim();
    if (fromJwt) return fromJwt;
    try {
      return localStorage.getItem('lastLoggedUsername')?.trim() || null;
    } catch {
      return null;
    }
  }

  private str(v: unknown): string | null {
    if (v == null) return null;
    const s = String(v).trim();
    return s || null;
  }
}
